import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useAnimation, useReducedMotion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon, ReplyIcon, ReplyAllIcon, ForwardIcon, MoreHorizontalIcon } from 'lucide-react';
const items = [{
  n: 1,
  title: 'ACORD Applications (125 & 131)'
}, {
  n: 2,
  title: 'TRIA Form'
}, {
  n: 3,
  title: 'Underlying GL Policy'
}, {
  n: 4,
  title: 'Supplemental Application'
}];
type RowState = 'pending' | 'matching' | 'done';
export function ChecklistMock() {
  const [states, setStates] = useState<RowState[]>(() => items.map(() => 'pending'));
  const [allDone, setAllDone] = useState(false);
  const cardControls = useAnimation();
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(rootRef, {
    amount: 0.3
  });
  useEffect(() => {
    // Reduced-motion: render the completed end-state, no loops
    if (reduced) {
      setStates(items.map(() => 'done'));
      setAllDone(true);
      return;
    }
    // Off-screen: don't schedule the loop until we're back in view
    if (!inView) return;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const STEP = 1500;
    const MATCH_DURATION = 700;
    const START_DELAY = 700;
    const HOLD_AFTER_COMPLETE = 2400;
    const setRow = (i: number, s: RowState) => {
      setStates(prev => {
        const next = [...prev];
        next[i] = s;
        return next;
      });
    };
    const run = () => {
      setStates(items.map(() => 'pending'));
      setAllDone(false);
      items.forEach((_, i) => {
        timeouts.push(setTimeout(() => setRow(i, 'matching'), START_DELAY + i * STEP));
        timeouts.push(setTimeout(() => {
          setRow(i, 'done');
          cardControls.start({
            scale: [1, 1.008, 1],
            transition: {
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }
          });
        }, START_DELAY + i * STEP + MATCH_DURATION));
      });
      timeouts.push(setTimeout(() => setAllDone(true), START_DELAY + items.length * STEP + 200));
      timeouts.push(setTimeout(run, START_DELAY + items.length * STEP + HOLD_AFTER_COMPLETE));
    };
    run();
    return () => timeouts.forEach(clearTimeout);
  }, [cardControls, reduced, inView]);
  const completed = states.filter(s => s === 'done').length;
  const progressPct = completed / items.length * 100;
  const firstPendingIdx = states.findIndex(s => s === 'pending');
  const timeLabels = ['Just now', '1m ago', '2m ago', '3m ago', '3m ago'];
  const timeLabel = timeLabels[completed] ?? '3m ago';
  return <motion.div ref={rootRef} animate={cardControls} className="relative w-full overflow-hidden rounded-xl border border-zinc-300/30 bg-white text-[#242424] shadow-2xl shadow-black/60" data-id="element-118">
      {/* EMAIL META — subject row with right-aligned reply actions, then sender + time below. */}
      <div className="px-4 pt-3 pb-2.5" data-id="element-119">
        <div className="flex items-start justify-between gap-3" data-id="element-120">
          <h3 className="min-w-0 flex-1 text-[13px] font-semibold leading-snug text-[#242424] sm:text-sm" data-id="element-121">
            Action Required: Submit Binding Documents
          </h3>
          {/* Reply / Reply all / Forward / More — desktop only, inline with subject */}
          <div className="hidden flex-shrink-0 items-center gap-0.5 text-[#605E5C] sm:flex" data-id="element-122">
            <button className="rounded p-1 hover:bg-[#F3F2F1]" aria-label="Reply" data-id="element-123">
              <ReplyIcon className="h-3 w-3" data-id="element-124" />
            </button>
            <button className="rounded p-1 hover:bg-[#F3F2F1]" aria-label="Reply all" data-id="element-125">
              <ReplyAllIcon className="h-3 w-3" data-id="element-126" />
            </button>
            <button className="rounded p-1 hover:bg-[#F3F2F1]" aria-label="Forward" data-id="element-127">
              <ForwardIcon className="h-3 w-3" data-id="element-128" />
            </button>
            <button className="rounded p-1 hover:bg-[#F3F2F1]" aria-label="More" data-id="element-129">
              <MoreHorizontalIcon className="h-3 w-3" data-id="element-130" />
            </button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2" data-id="element-131">
          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#0F6CBD] text-[10px] font-semibold text-white" data-id="element-132">
            JS
          </div>
          <span className="text-[11px] font-semibold text-[#242424]" data-id="element-133">
            John Smith
          </span>
          <span className="truncate text-[10px] text-[#605E5C]" data-id="element-134">
            &lt;jsmith@highpointins.com&gt;
          </span>
          <span className="ml-auto flex-shrink-0 text-[10px] text-[#605E5C]" data-id="element-135">
            <AnimatePresence mode="popLayout" data-id="element-136">
              <motion.span key={timeLabel} initial={{
              opacity: 0,
              y: -2
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 2
            }} transition={{
              duration: 0.25
            }} className="inline-block" data-id="element-137">
                {timeLabel}
              </motion.span>
            </AnimatePresence>
          </span>
        </div>
      </div>

      {/* PIGEON CHECKLIST — embedded in the email body */}
      <div className="px-4 pb-4" data-id="element-138">
        <div className="overflow-hidden rounded-lg border border-[#E1DFDD] bg-[#FAFAFA]" data-id="element-139">
          {/* Pigeon header — first-party brand, no "Powered by" */}
          <div className="flex items-center justify-between border-b border-[#E1DFDD] bg-white px-3 py-2" data-id="element-140">
            <div className="flex items-center gap-1.5" data-id="element-141">
              <div className="flex h-4 w-4 items-center justify-center rounded bg-accent/15" data-id="element-142">
                <SparklesIcon className="h-2.5 w-2.5 text-accent" data-id="element-143" />
              </div>
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#242424]" data-id="element-144">
                Pigeon AI
              </span>
            </div>
            <span className="font-mono text-[10px] tabular-nums text-[#323130]" data-id="element-145">
              <AnimatePresence mode="popLayout" data-id="element-146">
                <motion.span key={completed} initial={{
                opacity: 0,
                y: -3
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 3
              }} transition={{
                duration: 0.2
              }} className="inline-block" data-id="element-147">
                  {completed} / {items.length} complete
                </motion.span>
              </AnimatePresence>
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-[2px] w-full bg-[#EDEBE9]" data-id="element-148">
            <motion.div className="h-full bg-accent" animate={{
            width: `${progressPct}%`
          }} transition={{
            type: 'spring',
            stiffness: 80,
            damping: 18
          }} data-id="element-149" />
          </div>

          {/* Context line */}
          <div className="border-b border-[#E1DFDD] bg-white px-3 py-2" data-id="element-150">
            <div className="text-[12px] font-semibold text-[#242424]" data-id="element-151">
              Subjectivities · Seaview Systems
            </div>
            <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[#605E5C]" data-id="element-152">
              POL-112312333 · Bound Mar 14
            </div>
          </div>

          {/* Rows */}
          <ul className="divide-y divide-[#EDEBE9]" data-id="element-153">
            {items.map((it, i) => {
            const s = states[i];
            const isDone = s === 'done';
            const isMatching = s === 'matching';
            const showAwaiting = !isDone && !isMatching && i === firstPendingIdx;
            return <motion.li key={it.n} animate={{
              backgroundColor: isDone || isMatching ? 'rgba(56,189,248,0.08)' : 'rgba(255,255,255,1)'
            }} transition={{
              duration: 0.3
            }} className="relative flex items-center gap-2.5 px-3 py-2.5" data-id="element-154">
                  {/* Matching pulse ring */}
                  {isMatching && <motion.div aria-hidden initial={{
                opacity: 0
              }} animate={{
                opacity: [0, 0.6, 0]
              }} transition={{
                duration: 1,
                repeat: Infinity
              }} className="pointer-events-none absolute inset-x-2 inset-y-1 rounded ring-1 ring-accent/50" data-id="element-155" />}

                  {/* Status indicator */}
                  <div className="relative h-5 w-5 flex-shrink-0" data-id="element-156">
                    <AnimatePresence mode="wait" data-id="element-157">
                      {isDone ? <motion.div key="check" initial={{
                    scale: 0,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} exit={{
                    scale: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'backOut'
                  }} className="absolute inset-0 flex items-center justify-center rounded-full bg-accent" data-id="element-158">
                          <CheckIcon className="h-3 w-3 text-white" strokeWidth={3.5} data-id="element-159" />
                        </motion.div> : isMatching ? <motion.div key="matching" initial={{
                    scale: 0.6,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} exit={{
                    scale: 0.6,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="absolute inset-0 flex items-center justify-center" data-id="element-160">
                          <motion.div animate={{
                      rotate: 360
                    }} transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: 'linear'
                    }} className="h-4 w-4 rounded-full border-2 border-accent/20 border-t-accent" data-id="element-161" />
                        </motion.div> : <motion.div key="pending" initial={{
                    scale: 0.6,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} exit={{
                    scale: 0.6,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="absolute inset-0 flex items-center justify-center rounded-full border border-[#E1DFDD] bg-white font-mono text-[9px] text-[#605E5C]" data-id="element-162">
                          {it.n}
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Label + status */}
                  <div className="min-w-0 flex-1" data-id="element-163">
                    <div className={`text-[12px] font-medium leading-tight transition-colors ${isDone ? 'text-[#242424]' : isMatching ? 'text-[#242424]' : 'text-[#605E5C]'}`} data-id="element-164">
                      {it.title}
                    </div>
                    <div className="mt-0.5 h-3" data-id="element-165">
                      <AnimatePresence mode="wait" data-id="element-166">
                        {isDone ? <motion.div key="filed" initial={{
                      opacity: 0,
                      y: 2
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.25
                    }} className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-accent" data-id="element-167">
                            <span className="h-1 w-1 rounded-full bg-accent" data-id="element-168" />
                            Filed to AMS
                          </motion.div> : isMatching ? <motion.div key="matching-label" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#605E5C]" data-id="element-169">
                            Pigeon matching…
                          </motion.div> : showAwaiting ? <motion.div key="awaiting" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#A19F9D]" data-id="element-170">
                            Up next
                          </motion.div> : null}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.li>;
          })}
          </ul>

          {/* Footer beat — opacity + y only, no height animation */}
          <AnimatePresence data-id="element-171">
            {allDone && <motion.div key="footer-done" initial={{
            opacity: 0,
            y: -8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.35,
            ease: [0.16, 1, 0.3, 1]
          }} className="border-t border-accent/30 bg-accent/[0.08]" data-id="element-172">
                <div className="flex items-center justify-center gap-2 px-3 py-2.5" data-id="element-173">
                  <div className="flex h-4 w-4 items-center justify-center rounded-full bg-accent" data-id="element-174">
                    <CheckIcon className="h-2.5 w-2.5 text-white" strokeWidth={3.5} data-id="element-175" />
                  </div>
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-accent" data-id="element-176">
                    All filed · 0 follow-ups sent
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}